import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { masterAPI } from '../lib/api';
import { formatDateShort } from '../lib/utils';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import { 
  Plus, 
  Search, 
  Edit2, 
  Trash2, 
  Loader2, 
  ChevronLeft, 
  ChevronRight,
  Cog,
  Factory,
  Cpu,
  Wrench
} from 'lucide-react';

const masterConfig = {
  'machine-types': {
    title: 'Machine Types',
    icon: Cog,
    api: masterAPI,
    methods: {
      get: 'getMachineTypes',
      create: 'createMachineType',
      update: 'updateMachineType',
      delete: 'deleteMachineType',
    },
  },
  'manufacturers': {
    title: 'Manufacturers',
    icon: Factory,
    api: masterAPI,
    methods: {
      get: 'getManufacturers',
      create: 'createManufacturer',
      update: 'updateManufacturer',
      delete: 'deleteManufacturer',
    },
  },
  'models': {
    title: 'Models',
    icon: Cpu,
    api: masterAPI,
    methods: {
      get: 'getModels',
      create: 'createModel',
      update: 'updateModel',
      delete: 'deleteModel',
    },
  },
  'part-names': {
    title: 'Part Names',
    icon: Wrench,
    api: masterAPI,
    methods: {
      get: 'getPartNames',
      create: 'createPartName',
      update: 'updatePartName',
      delete: 'deletePartName',
    },
  },
  'part-groups': {
    title: 'Part Groups',
    icon: Wrench,
    api: masterAPI,
    methods: {
      get: 'getPartGroups',
      create: 'createPartGroup',
      update: 'updatePartGroup',
      delete: 'deletePartGroup',
    },
  },
};

const MasterTable = () => {
  const { type } = useParams();
  const navigate = useNavigate();
  const config = masterConfig[type];

  const [items, setItems] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  
  // Modal states
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isDeleteOpen, setIsDeleteOpen] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [deletingItem, setDeletingItem] = useState(null);
  const [name, setName] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const pageSize = 10;

  useEffect(() => {
    if (!config) {
      navigate('/dashboard');
      return;
    }
    fetchItems();
  }, [type, page, search]);

  const fetchItems = useCallback(async () => {
    if (!config) return;
    
    setIsLoading(true);
    try {
      const response = await config.api[config.methods.get](page, pageSize, search);
      setItems(response.data.items);
      setTotalPages(response.data.total_pages);
      setTotal(response.data.total);
    } catch (error) {
      toast.error('Failed to fetch items');
    } finally {
      setIsLoading(false);
    }
  }, [config, page, search]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!name.trim()) {
      toast.error('Name is required');
      return;
    }
    
    setIsSubmitting(true);
    try {
      if (editingItem) {
        await config.api[config.methods.update](editingItem.id, name.trim());
        toast.success('Item updated successfully');
      } else {
        await config.api[config.methods.create](name.trim());
        toast.success('Item created successfully');
      }
      setIsDialogOpen(false);
      setEditingItem(null);
      setName('');
      fetchItems();
    } catch (error) {
      const message = error.response?.data?.error || error.response?.data?.detail || 'Operation failed';
      toast.error(message);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDelete = async () => {
    if (!deletingItem) return;
    
    try {
      await config.api[config.methods.delete](deletingItem.id);
      toast.success('Item deleted successfully');
      setIsDeleteOpen(false);
      setDeletingItem(null);
      fetchItems();
    } catch (error) {
      toast.error('Failed to delete item');
    }
  };

  const openAddDialog = () => {
    setEditingItem(null);
    setName('');
    setIsDialogOpen(true);
  };

  const openEditDialog = (item) => {
    setEditingItem(item);
    setName(item.name);
    setIsDialogOpen(true);
  };

  const openDeleteDialog = (item) => {
    setDeletingItem(item);
    setIsDeleteOpen(true);
  };

  if (!config) {
    return null;
  }

  const Icon = config.icon;

  return (
    <div className="animate-fade-in" data-testid={`master-${type}-page`}>
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-slate-100 rounded-lg">
            <Icon className="w-5 h-5 text-slate-600" />
          </div>
          <div>
            <h1 
              className="text-2xl font-bold text-slate-900"
              style={{ fontFamily: 'Manrope, sans-serif' }}
            >
              {config.title}
            </h1>
            <p className="text-sm text-slate-500">{total} total records</p>
          </div>
        </div>
        <Button 
          onClick={openAddDialog}
          className="bg-slate-900 hover:bg-slate-800"
          data-testid="add-item-btn"
        >
          <Plus className="w-4 h-4 mr-2" />
          Add New
        </Button>
      </div>

      {/* Search */}
      <div className="bg-white rounded-lg border border-slate-200 p-4 mb-4">
        <div className="relative max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <Input
            placeholder={`Search ${config.title.toLowerCase()}...`}
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setPage(1);
            }}
            className="pl-10"
            data-testid="search-input"
          />
        </div>
      </div>

      {/* Table */}
      <div className="bg-white rounded-lg border border-slate-200 overflow-hidden">
        {isLoading ? (
          <div className="flex items-center justify-center h-64">
            <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
          </div>
        ) : items.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-64 text-slate-500">
            <Icon className="w-12 h-12 mb-4 opacity-20" />
            <p>No {config.title.toLowerCase()} found</p>
            {search && (
              <Button 
                variant="link" 
                onClick={() => setSearch('')}
                className="mt-2"
              >
                Clear search
              </Button>
            )}
          </div>
        ) : (
          <>
            <table className="data-table">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Created</th>
                  <th>Updated</th>
                  <th className="text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {items.map((item) => (
                  <tr key={item.id} data-testid={`item-row-${item.id}`}>
                    <td className="font-medium text-slate-900">{item.name}</td>
                    <td>{formatDateShort(item.created_at)}</td>
                    <td>{formatDateShort(item.updated_at)}</td>
                    <td className="text-right">
                      <div className="flex items-center justify-end gap-2">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => openEditDialog(item)}
                          data-testid={`edit-btn-${item.id}`}
                        >
                          <Edit2 className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => openDeleteDialog(item)}
                          className="text-red-600 hover:text-red-700 hover:bg-red-50"
                          data-testid={`delete-btn-${item.id}`}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="flex items-center justify-between px-4 py-3 border-t border-slate-200">
                <p className="text-sm text-slate-500">
                  Page {page} of {totalPages}
                </p>
                <div className="flex items-center gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setPage(p => Math.max(1, p - 1))}
                    disabled={page === 1}
                    data-testid="prev-page-btn"
                  >
                    <ChevronLeft className="w-4 h-4" />
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                    disabled={page === totalPages}
                    data-testid="next-page-btn"
                  >
                    <ChevronRight className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {/* Add/Edit Dialog */}
      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent data-testid="item-dialog">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Manrope, sans-serif' }}>
              {editingItem ? 'Edit Item' : 'Add New Item'}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit}>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label htmlFor="name">Name</Label>
                <Input
                  id="name"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Enter name"
                  data-testid="item-name-input"
                />
              </div>
            </div>
            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => setIsDialogOpen(false)}
              >
                Cancel
              </Button>
              <Button 
                type="submit" 
                disabled={isSubmitting}
                className="bg-slate-900 hover:bg-slate-800"
                data-testid="save-item-btn"
              >
                {isSubmitting ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : editingItem ? (
                  'Update'
                ) : (
                  'Create'
                )}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <AlertDialog open={isDeleteOpen} onOpenChange={setIsDeleteOpen}>
        <AlertDialogContent data-testid="delete-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Are you sure?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently delete "{deletingItem?.name}". This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction 
              onClick={handleDelete}
              className="bg-red-600 hover:bg-red-700"
              data-testid="confirm-delete-btn"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default MasterTable;
